import { useState, useEffect } from "react";
import { Menu, X, User } from "lucide-react";
import { useBooking } from "@/context/BookingContext";
import { goToTimelyAccount } from "@/lib/booking";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const { openBooking } = useBooking();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const ink = scrolled ? "text-[#1C1917]" : "text-white";
  const sub = scrolled ? "text-[#1C1917]/60" : "text-white/70";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#FAF8F5]/90 backdrop-blur-xl border-b border-[#1C1917]/10" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-5 sm:px-10 h-[72px] flex items-center justify-between">
        <a href="#home" data-testid="nav-logo" className={`leading-none ${ink}`}>
          <span className="font-serif text-xl sm:text-2xl font-semibold tracking-tight">Slavic</span>
          <span className={`hidden sm:inline text-[10px] tracking-[0.4em] ml-2 ${sub}`}>LASHES &amp; BROWS</span>
        </a>

        <div className="hidden lg:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={`${sub} hover:text-[#C5B382] text-[12px] font-medium tracking-[0.15em] uppercase transition-colors duration-300`}
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <button
            data-testid="nav-account"
            onClick={goToTimelyAccount}
            className={`hidden sm:inline-flex items-center gap-2 ${sub} hover:text-[#C5B382] text-[12px] font-medium tracking-[0.15em] uppercase transition-colors duration-300`}
          >
            <User size={16} /> My Account
          </button>
          <button
            data-testid="nav-book-now"
            onClick={() => openBooking()}
            className="hidden sm:inline-flex items-center bg-[#C5B382] hover:bg-[#B8A572] text-[#1C1917] text-[11px] font-semibold tracking-[0.15em] uppercase px-7 py-3 rounded-full transition-colors duration-300"
          >
            Book Now
          </button>
          <button
            data-testid="nav-hamburger"
            onClick={() => setMenuOpen(true)}
            className={`lg:hidden p-1 ${ink}`}
            aria-label="Open menu"
          >
            <Menu size={26} strokeWidth={1.5} />
          </button>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-[#1C1917]/40 backdrop-blur-sm" onClick={() => setMenuOpen(false)} />
        <div
          className={`absolute right-0 top-0 h-full w-[84%] max-w-sm bg-[#FAF8F5] p-8 flex flex-col transition-transform duration-500 ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex justify-between items-center mb-14">
            <span className="text-[#1C1917]/40 text-[11px] tracking-[0.4em] uppercase">Menu</span>
            <button data-testid="nav-close" onClick={() => setMenuOpen(false)} className="text-[#1C1917]">
              <X size={24} strokeWidth={1.5} />
            </button>
          </div>
          <div className="flex flex-col gap-6">
            {LINKS.map((l) => (
              <a
                key={l.label}
                href={l.href}
                data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                onClick={() => setMenuOpen(false)}
                className="text-[#1C1917] font-serif text-4xl font-medium hover:text-[#C5B382] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </div>
          <button
            data-testid="mobile-book-now"
            onClick={() => { setMenuOpen(false); openBooking(); }}
            className="mt-auto bg-[#C5B382] text-[#1C1917] text-xs font-semibold tracking-[0.2em] uppercase py-4 rounded-full"
          >
            Book Your Appointment
          </button>
          <button
            data-testid="mobile-account"
            onClick={() => { setMenuOpen(false); goToTimelyAccount(); }}
            className="mt-3 flex items-center justify-center gap-2 border border-[#1C1917]/15 text-[#1C1917] text-xs font-semibold tracking-[0.2em] uppercase py-4 rounded-full"
          >
            <User size={15} /> My Account
          </button>
        </div>
      </div>
    </header>
  );
}
